
import React, { useState, useEffect, useRef } from 'react'
import type { NodeOrchestratorState } from '../types'
import type { NodeType } from '@genieartinc/schemas'

const STAGE_SETS: Partial<Record<NodeType, string[]>> = {
  image: [
    'Interpreting your prompt...',
    'Composing the scene...',
    'Sampling from the model...',
    'Rendering light and shadow...',
    'Shaping forms and textures...',
    'Refining details...',
    'Enhancing colors...',
    'Almost ready...',
  ],
  video: [
    'Planning the motion...',
    'Generating keyframes...',
    'Calculating motion vectors...',
    'Rendering frames...',
    'Applying temporal consistency...',
    'Encoding the sequence...',
    'Smoothing transitions...',
    'Almost ready...',
  ],
  '3d': [
    'Analyzing geometry...',
    'Generating mesh structure...',
    'Calculating surface normals...',
    'Building material maps...',
    'Applying textures...',
    'Optimizing polygon count...',
    'Rendering preview...',
    'Almost ready...',
  ],
  text: [
    'Processing your prompt...',
    'Generating tokens...',
    'Structuring the response...',
    'Refining language...',
    'Almost ready...',
  ],
  shader: [
    'Parsing shader logic...',
    'Compiling to GPU code...',
    'Rendering to canvas...',
    'Optimizing performance...',
    'Almost ready...',
  ],
  upscale: [
    'Analyzing source image...',
    'Detecting edges and fine details...',
    'Enhancing resolution...',
    'Sharpening textures...',
    'Reconstructing high-frequency detail...',
    'Almost ready...',
  ],
  outpaint: [
    'Analyzing image boundaries...',
    'Planning the extended scene...',
    'Generating new regions...',
    'Matching lighting and style...',
    'Blending edges seamlessly...',
    'Almost ready...',
  ],
  controlnet: [
    'Extracting structural guidance...',
    'Conditioning the model...',
    'Generating guided output...',
    'Aligning to structure...',
    'Refining details...',
    'Almost ready...',
  ],
  mask: [
    'Analyzing subject boundaries...',
    'Generating mask...',
    'Refining edges...',
    'Almost ready...',
  ],
  'background-remover': [
    'Detecting subject boundaries...',
    'Separating foreground...',
    'Refining edge details...',
    'Cleaning up transparency...',
    'Almost ready...',
  ],
  describer: [
    'Examining the image...',
    'Identifying key elements...',
    'Analyzing composition...',
    'Understanding context...',
    'Writing description...',
    'Almost ready...',
  ],
  'lip-sync': [
    'Analyzing audio waveform...',
    'Detecting speech phonemes...',
    'Mapping mouth movements...',
    'Syncing to video frames...',
    'Rendering output...',
    'Almost ready...',
  ],
  'extract-video-frame': [
    'Reading video...',
    'Seeking to frame...',
    'Extracting frame data...',
    'Almost ready...',
  ],
  blur: [
    'Analyzing image...',
    'Applying blur kernel...',
    'Processing...',
    'Almost ready...',
  ],
  levels: [
    'Analyzing histogram...',
    'Adjusting tonal range...',
    'Processing...',
    'Almost ready...',
  ],
  crop: [
    'Calculating crop region...',
    'Processing...',
    'Almost ready...',
  ],
}

const BRAILLE_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏']

function useBrailleSpinner(active: boolean) {
  const [frame, setFrame] = useState(0)
  useEffect(() => {
    if (!active) { setFrame(0); return }
    const id = setInterval(() => setFrame(f => (f + 1) % BRAILLE_FRAMES.length), 80)
    return () => clearInterval(id)
  }, [active])
  return BRAILLE_FRAMES[frame]
}

const FALLBACK_STAGES = [
  'Starting up...',
  'Processing...',
  'Working on it...',
  'Almost ready...',
]

interface NodeProcessLogProps {
  orchestratorState?: NodeOrchestratorState
  nodeType?: NodeType
  inline?: boolean
}

export function NodeProcessLog({ orchestratorState, nodeType, inline }: NodeProcessLogProps) {
  const stages = (nodeType && STAGE_SETS[nodeType]) ?? FALLBACK_STAGES
  const [currentStage, setCurrentStage] = useState(0)
  const [animKey, setAnimKey] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isWorking = orchestratorState?.status === 'working'
  const spinner = useBrailleSpinner(isWorking)

  useEffect(() => {
    if (!isWorking) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setCurrentStage(0)
      setAnimKey(0)
      return
    }

    setCurrentStage(0)
    setAnimKey(k => k + 1)

    intervalRef.current = setInterval(() => {
      setCurrentStage(prev => {
        // Stop at the last stage, don't loop
        if (prev >= stages.length - 1) return prev
        return prev + 1
      })
      setAnimKey(k => k + 1)
    }, 2000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isWorking, stages.length])

  if (!isWorking) return null

  const activeMsg = stages[currentStage]

  if (inline) {
    return (
      <div className="flex items-center gap-2 h-8 min-w-0 pl-1">
        <span
          key={animKey}
          className="text-xs text-muted-foreground truncate"
          style={{ animation: 'nodeLogFadeIn 0.35s ease forwards' }}
        >
          {activeMsg}
        </span>
        <span className="flex-shrink-0 text-xs font-mono text-violet-500 leading-none">
          {spinner}
        </span>
        <style>{`
          @keyframes nodeLogFadeIn {
            from { opacity: 0; transform: translateY(3px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="border-t border-border px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-500 animate-pulse" />
        <span
          key={animKey}
          className="text-xs leading-none text-muted-foreground"
          style={{ animation: 'nodeLogFadeIn 0.35s ease forwards' }}
        >
          {activeMsg}
        </span>
      </div>
      <style>{`
        @keyframes nodeLogFadeIn {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
